import { type InputHTMLAttributes, type ReactNode, forwardRef } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../../lib/utils";

const inputVariants = cva(
  [
    "flex flex-col",
    "rounded-xl",
    "border border-solid",
    "overflow-clip",
    "transition-colors duration-150 ease-out",
  ],
  {
    variants: {
      variant: {
        default: [
          "bg-[rgba(255,255,255,0.85)]",
          "border-[rgba(0,0,0,0.25)]",
          "shadow-[0px_2px_8px_rgba(0,0,0,0.08)]",
          "focus-within:border-[rgba(0,0,0,0.4)]",
        ],
        ghost: [
          "bg-transparent",
          "border-transparent",
          "shadow-none",
          "focus-within:border-[rgba(0,0,0,0.25)]",
        ],
      },
      // lg is the phone's 52 touch target; md the desktop field (Figma Input
      // Size=md, 40 high).
      size: {
        lg: [],
        md: ["h-10", "justify-center", "[&>div]:py-0"],
      },
    },
    defaultVariants: {
      variant: "default",
      size: "lg",
    },
  }
);

type InputProps = Omit<InputHTMLAttributes<HTMLInputElement>, "size"> &
  VariantProps<typeof inputVariants> & {
    trailing?: ReactNode;
  };

const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, variant, size, trailing, ...props },
  ref
) {
  return (
    <div className={cn(inputVariants({ variant, size }), className)}>
      <div
        className={cn(
          "flex items-center gap-2",
          trailing != null ? "h-[52px] pl-3 pr-2 py-2" : "px-4 py-3"
        )}
      >
        <input
          ref={ref}
          className={cn(
            "flex-1 min-w-0 bg-transparent",
            "font-['DM_Sans',sans-serif] text-[15px] leading-[1.4] font-normal",
            "text-[#2d2d2d] placeholder:text-[rgba(122,109,101,0.5)]",
            "focus:outline-none",
            "font-features-['salt'_1]",
            "tracking-[-0.15px]"
          )}
          {...props}
        />
        {trailing}
      </div>
    </div>
  );
});

export { Input, inputVariants, type InputProps };
